<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Call History</h1>
        <p class="text-gray-500 mt-1">View and analyze all calls with your elderly loved ones</p>
      </div>
      <div class="flex space-x-2">
        <button
          @click="refreshCalls"
          :disabled="loading"
          class="btn btn-secondary flex items-center space-x-2"
        >
          <ArrowPathIcon class="w-5 h-5" />
          <span>Refresh</span>
        </button>
        <button
          @click="showQuickCall = true"
          class="btn btn-primary flex items-center space-x-2"
        >
          <PhoneIcon class="w-5 h-5" />
          <span>New Call</span>
        </button>
      </div>
    </div>
    
    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Total Calls</p>
              <p class="text-2xl font-bold">{{ callStats.total }}</p>
            </div>
            <PhoneIcon class="w-8 h-8 text-primary-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Completed</p>
              <p class="text-2xl font-bold text-success-600">{{ callStats.completed }}</p>
            </div>
            <CheckCircleIcon class="w-8 h-8 text-success-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Missed</p>
              <p class="text-2xl font-bold text-danger-600">{{ callStats.missed }}</p>
            </div>
            <XCircleIcon class="w-8 h-8 text-danger-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">In Progress</p>
              <p class="text-2xl font-bold text-warning-600">{{ callStats.inProgress }}</p>
            </div>
            <ArrowPathIcon class="w-8 h-8 text-warning-500 animate-spin" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Filters -->
    <div class="card">
      <div class="card-body">
        <div class="flex flex-wrap gap-4">
          <div class="flex-1 min-w-[200px]">
            <input
              v-model="filters.search"
              type="text"
              placeholder="Search by name or phone..."
              class="input w-full"
            />
          </div>
          <select v-model="filters.profile" class="input">
            <option value="">All Profiles</option>
            <option
              v-for="profile in profiles"
              :key="profile.id"
              :value="profile.id"
            >
              {{ profile.name }}
            </option>
          </select>
          <select v-model="filters.status" class="input">
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
            <option value="in_progress">In Progress</option>
            <option value="scheduled">Scheduled</option>
          </select>
          <input
            v-model="filters.date"
            type="date"
            class="input"
          />
          <button
            @click="resetFilters"
            class="btn btn-secondary"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
    
    <!-- Call List -->
    <div class="card">
      <div class="card-body p-0">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="spinner"></div>
        </div>
        
        <div v-else-if="filteredCalls.length === 0" class="text-center py-12">
          <PhoneXMarkIcon class="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p class="text-gray-500">No calls found</p>
        </div>
        
        <table v-else class="w-full">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Elder
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date & Time
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Wellness Score
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Alerts
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="call in paginatedCalls"
              :key="call.id"
              class="hover:bg-gray-50 cursor-pointer"
              @click="viewCallDetails(call)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="text-2xl mr-3">{{ getProfileById(call.profile_id)?.avatar || '👤' }}</div>
                  <div>
                    <div class="text-sm font-medium text-gray-900">{{ getProfileById(call.profile_id)?.name || 'Unknown' }}</div>
                    <div class="text-sm text-gray-500">{{ getProfileById(call.profile_id)?.phone || 'N/A' }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">{{ formatDate(call.initiated_at) }}</div>
                <div class="text-sm text-gray-500">{{ formatTime(call.initiated_at) }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm text-gray-900">{{ formatDuration(call.duration_seconds) }}</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="['badge', getStatusBadgeClass(call.status)]">
                  {{ call.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="w-16 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      :class="['h-2 rounded-full', getWellnessColor(getWellnessScore(call))]"
                      :style="`width: ${getWellnessScore(call)}%`"
                    ></div>
                  </div>
                  <span class="text-sm font-medium">{{ getWellnessScore(call) }}%</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div v-if="call.alerts_generated && call.alerts_generated.length > 0" class="flex items-center">
                  <ExclamationTriangleIcon class="w-5 h-5 text-warning-500 mr-1" />
                  <span class="text-sm text-warning-600">{{ call.alerts_generated.length }}</span>
                </div>
                <span v-else class="text-sm text-gray-400">None</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex space-x-2">
                  <button
                    v-if="call.status === 'in_progress'"
                    @click.stop="refreshCallStatus(call)"
                    class="text-blue-600 hover:text-blue-900"
                    title="Refresh Status"
                  >
                    <ArrowPathIcon class="w-5 h-5" />
                  </button>
                  <button
                    @click.stop="viewTranscript(call)"
                    class="text-primary-600 hover:text-primary-900"
                    title="View Transcript"
                  >
                    <DocumentTextIcon class="w-5 h-5" />
                  </button>
                  <button
                    @click.stop="playRecording(call)"
                    class="text-primary-600 hover:text-primary-900"
                    title="Play Recording"
                  >
                    <PlayIcon class="w-5 h-5" />
                  </button>
                  <button
                    @click.stop="downloadRecording(call)"
                    class="text-primary-600 hover:text-primary-900"
                    title="Download"
                  >
                    <ArrowDownTrayIcon class="w-5 h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Pagination -->
        <div v-if="totalPages > 1" class="px-6 py-4 border-t">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to 
              {{ Math.min(currentPage * itemsPerPage, filteredCalls.length) }} of 
              {{ filteredCalls.length }} results
            </div>
            <div class="flex space-x-2">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="currentPage = page"
                :class="[
                  'btn btn-sm',
                  page === currentPage ? 'btn-primary' : 'btn-secondary'
                ]"
              >
                {{ page }}
              </button>
              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Call Details Modal -->
    <CallDetailsModal
      v-if="selectedCall"
      :call="selectedCall"
      @close="selectedCall = null"
    />
    
    <!-- Quick Call Modal -->
    <QuickCallModal
      v-if="showQuickCall"
      @close="showQuickCall = false"
      @call="handleQuickCall"
    />
    
    <!-- Transcript Modal -->
    <TranscriptModal
      v-if="transcriptCall"
      :call="transcriptCall"
      @close="transcriptCall = null"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCallStore } from '@/stores/calls'
import { useProfileStore } from '@/stores/profiles'
import {
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PhoneXMarkIcon,
  DocumentTextIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import CallDetailsModal from '@/components/calls/CallDetailsModal.vue'
import QuickCallModal from '@/components/calls/QuickCallModal.vue'
import TranscriptModal from '@/components/calls/TranscriptModal.vue'
import dayjs from 'dayjs'

const callStore = useCallStore()
const profileStore = useProfileStore()

const loading = computed(() => callStore.loading)
const calls = computed(() => callStore.calls)
const callStats = computed(() => callStore.callStats)
const profiles = computed(() => profileStore.profiles)

const filters = ref({
  search: '',
  profile: '',
  status: '',
  date: ''
})

const currentPage = ref(1)
const itemsPerPage = 10
const selectedCall = ref(null)
const showQuickCall = ref(false)
const transcriptCall = ref(null)

const filteredCalls = computed(() => {
  let result = [...calls.value]
  
  if (filters.value.search) {
    const search = filters.value.search.toLowerCase()
    result = result.filter(call => {
      const profile = getProfileById(call.profile_id)
      return profile?.name.toLowerCase().includes(search) ||
             profile?.phone.includes(search)
    })
  }
  
  if (filters.value.profile) {
    result = result.filter(call => 
      call.profile_id === parseInt(filters.value.profile)
    )
  }
  
  if (filters.value.status) {
    result = result.filter(call => call.status === filters.value.status)
  }
  
  if (filters.value.date) {
    const filterDate = new Date(filters.value.date).toDateString()
    result = result.filter(call => 
      new Date(call.initiated_at).toDateString() === filterDate
    )
  }
  
  return result
})

const paginatedCalls = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredCalls.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredCalls.value.length / itemsPerPage)
})

const visiblePages = computed(() => {
  const pages = []
  const total = totalPages.value
  const current = currentPage.value
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    if (current <= 3) {
      for (let i = 1; i <= 5; i++) {
        pages.push(i)
      }
      pages.push('...', total)
    } else if (current >= total - 2) {
      pages.push(1, '...')
      for (let i = total - 4; i <= total; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1, '...')
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i)
      }
      pages.push('...', total)
    }
  }
  
  return pages.filter(p => p !== '...' || pages.indexOf(p) === pages.lastIndexOf(p))
})

function formatDate(date) {
  return dayjs(date).format('MMM DD, YYYY')
}

function formatTime(date) {
  return dayjs(date).format('h:mm A')
}

function formatDuration(seconds) {
  if (!seconds) return 'N/A'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getStatusBadgeClass(status) {
  const classes = {
    completed: 'badge-success',
    missed: 'badge-danger',
    in_progress: 'badge-warning',
    'in_progress': 'badge-warning', // Handle backend format
    scheduled: 'badge-info'
  }
  return classes[status] || 'badge-secondary'
}

function getWellnessColor(score) {
  if (score >= 80) return 'bg-success-500'
  if (score >= 60) return 'bg-warning-500'
  return 'bg-danger-500'
}

function getProfileById(profileId) {
  return profiles.value.find(p => p.id === profileId)
}

function getWellnessScore(call) {
  // Calculate overall wellness from wellness_scores object or return default
  if (call.wellness_scores && Object.keys(call.wellness_scores).length > 0) {
    const scores = Object.values(call.wellness_scores)
    const average = scores.reduce((sum, score) => sum + score, 0) / scores.length
    return Math.round(average)
  }
  return 0 // No wellness data available yet
}

function resetFilters() {
  filters.value = {
    search: '',
    profile: '',
    status: '',
    date: ''
  }
  currentPage.value = 1
}

function viewCallDetails(call) {
  selectedCall.value = call
}

function viewTranscript(call) {
  transcriptCall.value = call
}

function playRecording(call) {
  // Implement audio player
  console.log('Playing recording for call:', call.id)
}

async function downloadRecording(call) {
  await callStore.downloadCallRecording(call.id)
}

async function handleQuickCall(profileId) {
  try {
    await callStore.initiateCall(profileId)
    showQuickCall.value = false
    // Refresh calls list to show the new call
    await callStore.fetchCalls()
  } catch (error) {
    console.error('Failed to initiate call:', error)
  }
}

async function refreshCalls() {
  await callStore.fetchCalls()
}

async function refreshCallStatus(call) {
  try {
    await callStore.refreshCallStatus(call.id)
  } catch (error) {
    console.error('Failed to refresh call status:', error)
  }
}

onMounted(async () => {
  await Promise.all([
    callStore.fetchCalls(),
    profileStore.fetchProfiles()
  ])
})
</script>